import {
    computed,
    effect,
    EnvironmentProviders,
    inject,
    Injectable,
    Injector,
    makeEnvironmentProviders,
    provideEnvironmentInitializer,
    Signal,
    untracked,
} from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { ApplicationContextStore } from '../application-context/application-context.store';
import { settingState } from '../application-context/setting-state';
import { AuthService } from '../auth-service/auth.service';
import { RouteSegments } from '../router-service/route.constants';
import { ApplicationInsightsService, TelemetryStartOptions } from './application-insights.service';

/** Options for {@link TelemetryPolicyService.watch} and {@link provideTelemetryPolicy}. */
export interface TelemetryPolicyOptions {
    /**
     * Also send telemetry, anonymously, for customer sessions opened by a code login (shared report and self check-in
     * links). Off unless a shell asks for it, so a shell without customer links is unaffected.
     */
    customerSessions?: boolean;
}

/** Who telemetry was last asked to run for. Each is a separate run, because the authenticated user differs. */
type TelemetryAudience = 'staff' | 'customer';

/**
 * The sign-in pages: the staff sign-in, a customer link's verification page, and the landing page of an external
 * launch. Reaching any of them ends a customer session for telemetry, whether or not its token is still held.
 */
const SIGN_IN_SEGMENTS: readonly string[] = [RouteSegments.LOGIN, RouteSegments.CODE_LOGIN, RouteSegments.LAUNCH];

/**
 * Whether a router URL is one of the {@link SIGN_IN_SEGMENTS}. Only the first path segment is compared, so a query
 * string or a deeper route that merely contains the word does not match.
 *
 * @param url The router URL, as the application context records it.
 * @returns True on a sign-in page.
 */
export function isSignInUrl(url: string): boolean {
    const path = url.split(/[?#;]/)[0];
    const firstSegment = path.split('/').find((segment) => segment !== '') ?? '';
    return SIGN_IN_SEGMENTS.includes(firstSegment);
}

/**
 * Decides when client telemetry runs, so every shell applies the same rule: only once a user has signed in, and only
 * while the selected branch has the watched Boolean setting on. Shells wire it with {@link provideTelemetryPolicy}
 * and never call {@link ApplicationInsightsService.start} or {@link ApplicationInsightsService.stop} themselves.
 *
 * A shell that opts in to customer sessions also gets telemetry for a customer once a code login succeeds, with no
 * authenticated user and no branch setting (a customer cannot read branch settings). The same service decides both,
 * so the two rules cannot fight: a customer session takes precedence over a staff session the application context
 * still reports, and a change between the two stops the run before the next one starts, so a staff user id never
 * carries over into a customer's telemetry and a customer's anonymous run never carries over into a staff session.
 */
@Injectable({ providedIn: 'root' })
export class TelemetryPolicyService {
    private readonly applicationContext = inject(ApplicationContextStore);
    private readonly appInsights = inject(ApplicationInsightsService);
    private readonly injector = inject(Injector);
    private readonly startedSubject = new Subject<void>();

    /** Whether a sign-in has been seen this app session; signing out after one stops telemetry, before one it does not. */
    private hasSignedIn = false;

    /**
     * Who the policy has asked telemetry to run for, or null when it has not, so a setting that later goes missing
     * stops it (fail-closed) and a change of audience restarts it.
     */
    private requested: TelemetryAudience | null = null;

    private watching = false;

    /**
     * Emits each time telemetry has started running under this policy (after a sign-in, or after the setting turns on).
     * Shells use it for events that must only ever be sent once telemetry is allowed, such as a device login event.
     */
    readonly started: Observable<void> = this.startedSubject.asObservable();

    /**
     * Starts applying the policy for the given setting. Idempotent; the first call wins.
     *
     * @param settingKey The Boolean branch setting (a SettingKey enum member name) that allows telemetry.
     * @param options Whether customer sessions send telemetry too.
     */
    watch(settingKey: string, options: TelemetryPolicyOptions = {}): void {
        if (this.watching) {
            return;
        }
        this.watching = true;
        const allowed = computed(() => settingState(this.applicationContext.settings(), settingKey));
        const customerSession = options.customerSessions ? this.watchCustomerSession() : computed(() => false);
        effect(() => this.decide(this.applicationContext.isLoggedIn(), allowed(), customerSession()), { injector: this.injector });
    }

    /**
     * A customer session counts while the tab holds one and is not on a sign-in page. The page matters because a
     * customer can reach the staff sign-in or their link's verification page with a session that is about to be
     * replaced. AuthService is only resolved here, so a shell that does not opt in never constructs it for telemetry.
     */
    private watchCustomerSession(): Signal<boolean> {
        const authService = this.injector.get(AuthService);
        return computed(() => authService.customerSession() && !isSignInUrl(this.applicationContext.currentUrl()));
    }

    /**
     * Applies the customer rule, then the staff rule in {@link apply}. A customer session runs telemetry with no
     * authenticated user. When one ends the run stops at once, before the staff rule decides afresh; while signed out
     * that stop is all there is to do.
     */
    private decide(isLoggedIn: boolean, allowed: boolean | undefined, customerSession: boolean): void {
        if (customerSession) {
            this.hasSignedIn = true;
            this.request('customer', {});
            return;
        }
        if (this.requested === 'customer') {
            this.stop();
            if (!isLoggedIn) {
                return;
            }
        }
        this.apply(isLoggedIn, allowed);
    }

    /**
     * Starts telemetry once signed in on a branch that allows it, and stops it on sign-out or when the branch has it
     * off. Nothing is sent before sign-in: until then the service only queues exceptions, so start-up errors reach
     * telemetry only if the signed-in branch allows it. That is also why it never stops before the first sign-in: the
     * store reports signed-out while it restores the session at boot, and stopping then would discard that queue. A
     * missing setting (settings not loaded yet) starts nothing and stops telemetry only if it was asked to run, so the
     * queue survives the gap between sign-in and the settings arriving.
     *
     * The same restraint protects telemetry kept on the device while offline: every stop deletes it (see
     * {@link ApplicationInsightsService.stop}), so items an earlier run left behind are sent only if this session's
     * branch starts telemetry, and deleted once it is signed out or its branch has telemetry off.
     */
    private apply(isLoggedIn: boolean, allowed: boolean | undefined): void {
        if (!isLoggedIn) {
            if (this.hasSignedIn) {
                this.stop();
            }
            return;
        }
        this.hasSignedIn = true;

        if (allowed === true) {
            const authenticatedUserId = untracked(() => this.applicationContext.userId());
            this.request('staff', { authenticatedUserId });
        } else if (allowed === false || this.requested !== null) {
            this.stop();
        }
    }

    /**
     * Asks telemetry to run for the given audience, stopping a run for the other one first: a running service ignores
     * a second start, so without the stop it would keep the previous run's authenticated user. {@link started} is
     * reported for staff runs only, because what shells send on it (a device login event) belongs to a signed-in user.
     */
    private request(audience: TelemetryAudience, options: TelemetryStartOptions): void {
        if (this.requested !== null && this.requested !== audience) {
            this.stop();
        }
        this.requested = audience;
        this.appInsights.start(options).then((running) => {
            if (running && audience === 'staff' && this.requested === 'staff') {
                this.startedSubject.next();
            }
        });
    }

    private stop(): void {
        this.requested = null;
        this.appInsights.stop();
    }
}

/**
 * Applies the {@link TelemetryPolicyService} from application start, watching the given Boolean branch setting.
 *
 * @param settingKey The setting (a SettingKey enum member name) that allows telemetry for the selected branch.
 * @param options Whether customer sessions send telemetry too; off by default.
 * @returns Providers for the application config.
 */
export function provideTelemetryPolicy(settingKey: string, options: TelemetryPolicyOptions = {}): EnvironmentProviders {
    return makeEnvironmentProviders([provideEnvironmentInitializer(() => inject(TelemetryPolicyService).watch(settingKey, options))]);
}
